/**
 * CORE-2098 — «Вход истёк. Войдите снова, чтобы продолжить» (не «Сессия закончилась» — в эфире читалось как конец Live-сессии).
 *
 * Сигнал поднимает `utils/sessionExpiry` (401 при истёкшем токене); окно
 * показывается только тому, кто для приложения вошёл, — гостю входить снова
 * некуда. Окно не модальное: страница под ним живёт, пока человек не решит.
 * «Позже» закрывает его, «Войти» ведёт на вход и возвращает сюда же.
 *
 * Только веб (`SESSION_EXPIRY_PROMPT_ENABLED`): на нативе 401 ведёт на вход
 * по-прежнему.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Platform, Text, TouchableOpacity, View } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';

import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import { analytics } from '../services/analytics';
import { reloginAfterSessionExpiry } from '../navigation/sessionExpiryRelogin';
import {
  resetSessionExpired,
  SESSION_EXPIRY_PROMPT_ENABLED,
  snoozeSessionExpired,
  subscribeSessionExpired,
} from '../utils/sessionExpiry';
import { typographyTokens } from '../styles/typographyTokens';
import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../styles/accentText';

export function SessionExpiredPrompt() {
  const { user, expireSession } = useAuth();
  const { colors } = useTheme();
  const { t } = useI18n('auth');
  const [visible, setVisible] = useState(false);
  const signedInRef = useRef(false);
  signedInRef.current = !!user;

  useEffect(() => {
    if (!SESSION_EXPIRY_PROMPT_ENABLED) return undefined;
    return subscribeSessionExpired(() => {
      if (!signedInRef.current) return;
      setVisible(true);
      analytics.track('session_expired_prompt_shown');
    });
  }, []);

  // Вошёл другой человек или тот же заново (или вышел) — всё сначала.
  useEffect(() => {
    resetSessionExpired();
    setVisible(false);
  }, [user?.id]);

  if (!SESSION_EXPIRY_PROMPT_ENABLED || !visible || !user) return null;

  const onLogin = () => {
    setVisible(false);
    analytics.track('session_expired_relogin');
    void reloginAfterSessionExpiry(expireSession);
  };
  const onLater = () => {
    snoozeSessionExpired();
    setVisible(false);
  };

  return (
    <View
      pointerEvents="box-none"
      style={{
        position: Platform.OS === 'web' ? 'fixed' : 'absolute',
        top: 16,
        left: 16,
        right: 16,
        alignItems: 'center',
        zIndex: 10000,
      } as any}
    >
      <View
        testID="session-expired-prompt"
        accessibilityRole="alert"
        style={{
          backgroundColor: colors.surfaceDialog,
          borderRadius: 16,
          padding: 20,
          maxWidth: 420,
          width: '100%',
          gap: 12,
          ...(Platform.OS === 'web' && { boxShadow: '0 12px 40px rgba(0,0,0,0.18)' }),
        } as any}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Ionicons name="time-outline" size={22} color={ACCENT_TEXT} />
          <Text style={{ ...typographyTokens.bodyAccent, color: colors.textPrimary, flex: 1 }}>
            {t('sessionExpired.title')}
          </Text>
        </View>
        <Text style={{ ...typographyTokens.body, color: colors.textSecondary }}>
          {t('sessionExpired.text')}
        </Text>
        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8 }}>
          <TouchableOpacity
            testID="session-expired-later"
            onPress={onLater}
            accessibilityRole="button"
            activeOpacity={0.7}
            style={{ paddingHorizontal: 16, paddingVertical: 10, borderRadius: 12 }}
          >
            <Text style={{ ...typographyTokens.buttonSecondary, color: colors.textSecondary }}>
              {t('sessionExpired.later')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            testID="session-expired-login"
            onPress={onLogin}
            accessibilityRole="button"
            activeOpacity={0.85}
            style={{ backgroundColor: '#E85D04', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 12 }}
          >
            <Text style={{ ...typographyTokens.buttonPrimary, color: ON_ACCENT_TEXT }}>
              {t('sessionExpired.login')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}
